import { InputAdornment } from "@mui/material";
import { useEvents } from "api/events";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useMessage } from "hooks/intl";
import { get } from "lodash";
import { useState } from "react";
import { ListResponse } from "types/listing";
import { LastCustomEventWasNDaysAgo as LastCustomEventWasNDaysAgoType } from "types/segments";
import { formatNumberValue } from "utilities/numbers";
import { pluralizeValueAdornment } from "utilities/strings";
import { BoxElement } from "./BoxElement";
import { useStyles } from "./SegmentConditions.styles";

export const initialValuesLastCustomEventWasNDaysAgo: LastCustomEventWasNDaysAgoType =
  {
    days: 1,
    type: "last_custom_event_n_days_before",
    customEventType: undefined,
  };

export function LastCustomEventWasNDaysAgo({
  formField,
  values,
  status,
  readOnly,
}: {
  formField: string;
  values: any;
  status: any;
  readOnly?: boolean;
}) {
  const { message } = useMessage();
  const value = get(values, formField);
  const daysError = get(status, "days");
  const daysPluralized = message({
    id: pluralizeValueAdornment("day", value.days),
  }).toLowerCase();
  const classes = useStyles();
  const [customEventsSearch, setSearchEvent] = useState<string>();
  const { useListOfCustomEventsSchema, useCustomEventSchemaDetails } =
    useEvents();
  const { data: listOfCustomEvents } = useListOfCustomEventsSchema({
    filters: customEventsSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: customEventsSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    rows: 10,
    sortKey: "name",
  }) as {
    data: ListResponse;
  };
  const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(
    value?.customEventType,
  );

  const initialValue = selectedSchemaDetails && {
    id: selectedSchemaDetails.eventType as string,
    label: selectedSchemaDetails.name,
  };

  const eventIdError = get(status, "customEventType");

  return (
    <>
      {value.isConditionSaved || readOnly ? (
        <>
          <BoxElement
            content={
              value?.customEventType
                ? listOfCustomEvents?.items?.find(
                    (item) => item.eventType === value?.customEventType,
                  )?.name || value.customEventType
                : message({ id: "anyCustomEvent" })
            }
            error={eventIdError}
          />
          {message({ id: "is_equal" }).toLocaleLowerCase()}
          <BoxElement
            content={formatNumberValue(value?.days, "")}
            error={daysError}
          />
          {daysPluralized}
        </>
      ) : (
        <>
          <SelectInput
            asyncSearch={(searchedQuery: string) =>
              setSearchEvent(searchedQuery)
            }
            allowUndefinedAsItemValue
            className={classes.input}
            data-test-id="segment-conditions-custom-event-name-input"
            displaySearch
            initialValue={initialValue}
            items={[
              { id: undefined, label: message({ id: "anyCustomEvent" }) },
              ...(listOfCustomEvents?.items || [])?.map(
                (event: { eventType: string; name: string }) => ({
                  label: event.name,
                  id: event.eventType,
                }),
              ),
            ]}
            label={<RequiredFieldLabel text={message({ id: "customEvent" })} />}
            name={`${formField}.customEventType`}
            placeholder={message({ id: "eventName" })}
          />
          <NumberFormatInput
            allowNegative={false}
            className={classes.input}
            data-test-id="segment-conditions-custom-event-within-num-of-days-input"
            label={<RequiredFieldLabel text={message({ id: "days" })} />}
            name={`${formField}.days`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{daysPluralized}</InputAdornment>
              ),
            }}
          />
        </>
      )}
    </>
  );
}
